// Single source for the portal sidebar and the overview page headings.
import {
    mdiBookOpenPageVariantOutline, mdiChartLine, mdiClockOutline, mdiCogOutline, mdiDotsGrid, mdiLifebuoy,
    mdiLockOutline, mdiReceiptTextOutline, mdiShieldAccountOutline, mdiSitemapOutline
} from '@mdi/js';

export type OverviewSection = 'flow' | 'statistiek' | 'flowplanner' | 'credentials' | 'settings' | 'license';

export const HANDLEIDING_URL = 'https://app.simplexconnect.nl/portal/help';
export const SUPPORT_CENTER_URL = 'https://portal.simplex.nl';

export interface NavItem {
    label: string;
    // MDI SVG path, the same icon set as the AppPortal
    icon: string;
    to?: string;
    href?: string;
    external?: boolean;
    adminOnly?: boolean;
    superAdminOnly?: boolean;
}

export interface NavGroup {
    caption: string;
    items: Array<NavItem>;
    // Pinned to the bottom of the sidebar instead of following the other groups
    bottom?: boolean;
}

export interface SectionMeta {
    title: string;
    description: string;
    icon: string;
    adminOnly?: boolean;
}

export const overviewSections: Record<OverviewSection, SectionMeta> = {
    flow: {
        title: 'Flows',
        description: 'Beheer, open en monitor de Flows van je organisatie.',
        icon: mdiSitemapOutline,
    },
    statistiek: {
        title: 'Statistiek',
        description: 'Gebruik en recente uitvoeringen van je Flows.',
        icon: mdiChartLine,
    },
    flowplanner: {
        title: 'Flow Planner',
        description: 'Plan Flows in, start ze eenmalig of koppel ze aan een Endpoint.',
        icon: mdiClockOutline,
    },
    credentials: {
        title: 'Wachtwoorden & Tokens',
        description: 'Versleuteld opgeslagen wachtwoorden, tokens en integraties.',
        icon: mdiLockOutline,
    },
    settings: {
        title: 'Instellingen',
        description: 'Import/export, e-mail, kantoortijden en gearchiveerde Flows.',
        icon: mdiCogOutline,
    },
    license: {
        title: 'Licentie',
        description: 'Bekijk en wijzig het abonnement van je organisatie.',
        icon: mdiReceiptTextOutline,
        adminOnly: true,
    },
};

export const isOverviewSection = (value: unknown): value is OverviewSection =>
    typeof value === 'string' && value in overviewSections;

const sectionItem = (key: OverviewSection): NavItem => ({
    label: overviewSections[key].title,
    icon: overviewSections[key].icon,
    to: `/overview/${key}`,
    adminOnly: overviewSections[key].adminOnly,
});

export const navigation: Array<NavGroup> = [
    {
        caption: 'Overzicht',
        items: [sectionItem('flow'), sectionItem('statistiek'), sectionItem('flowplanner')],
    },
    {
        caption: 'Beheer',
        items: [
            sectionItem('credentials'),
            sectionItem('settings'),
            sectionItem('license'),
            { label: 'Admin', icon: mdiShieldAccountOutline, to: '/admin', superAdminOnly: true },
        ],
    },
    {
        caption: 'Ondersteuning',
        bottom: true,
        items: [
            { label: 'Handleiding', icon: mdiBookOpenPageVariantOutline, href: HANDLEIDING_URL, external: true },
            { label: 'Support Center', icon: mdiLifebuoy, href: SUPPORT_CENTER_URL, external: true },
            { label: 'AppStore', icon: mdiDotsGrid, href: 'https://app.simplexconnect.nl', external: false },
        ],
    },
];

export const APPSTORE_ORGANIZATIONS_URL = 'https://app.simplexconnect.nl/?screen=organizations';
